import { FaQuestion, FaAngleRight, FaAngleDown, FaAngleUp, FaBitcoin, FaRegClock, FaClock, FaCloudSun, FaImage, FaEye, FaEyeSlash, FaSpotify, FaTriangleExclamation, FaHourglassStart, FaPaintbrush, FaTwitch } from "react-icons/fa6";
import { MdTextsms } from "react-icons/md";
import { BsGraphUpArrow } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SceneCrypto as SceneCryptoType, SceneText as SceneTextType, SceneClock as SceneClockType, SceneWeather as SceneWeatherType, SceneGif as SceneGifType, SceneOff as SceneOffType, SceneSpotify as SceneSpotifyType, Scene as TScene, SceneCountdown as SceneCountdownType, SceneCanvas as SceneCanvasType, SceneStock as SceneStockType, SceneTwitch as SceneTwitchType, TextLine } from "@ltypes/scene";
import SceneType from "@enums/sceneType";
import Card from "@components/ui/Card/Card";
import SceneCrypto from "@components/scenes/SceneCrypto/SceneCrypto";
import SceneText from "@components/scenes/SceneText/SceneText";
import SceneClock from "@components/scenes/SceneClock/SceneClock";
import SceneWeather from "@components/scenes/SceneWeather/SceneWeather";
import SceneGif from "@components/scenes/SceneGif/SceneGif";
import SceneOff from "@components/scenes/SceneOff/SceneOff";
import SceneSpotify from "@components/scenes/SceneSpotify/SceneSpotify";
import SceneCountdown from "@components/scenes/SceneCountdown/SceneCountdown";
import SceneCanvas from "@components/scenes/SceneCanvas/SceneCanvas";
import SceneStock from "@components/scenes/SceneStock/SceneStock";
import SceneTwitch from "@components/scenes/SceneTwitch/SceneTwitch";
import React, { useState } from "react";
import Button from "@components/forms/Button/Button";
import User, { UserApiCredential } from "@ltypes/user";
import "./Scene.scss";

type SceneProps = {
    scene: TScene,
    new?: boolean,
    isFirst?: boolean,
    isLast?: boolean,
    onChange: (field: string, value: any) => void,
    onSave: () => void,
    onDelete: () => void,
    onToggleEnabled: (enabled: boolean) => void,
    onMoveUp: () => void,
    onMoveDown: () => void,
};

function Scene(props: SceneProps) {
    const [isOpen, setOpen] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);

    const user: User|null = useSelector((state: any) => state.user);

    const { t } = useTranslation();

    const typeLabels = [
        t('page.scenes.type.crypto'),
        t('page.scenes.type.text'),
        t('page.scenes.type.clock'),
        t('page.scenes.type.weather'),
        t('page.scenes.type.gif'),
        t('page.scenes.type.timer'),
        t('page.scenes.type.pomodoro'),
        t('page.scenes.type.off'),
        t('page.scenes.type.spotify'),
        t('page.scenes.type.countdown'),
        t('page.scenes.type.canvas'),
        t('page.scenes.type.stock'),
        t('page.scenes.type.twitch'),
    ];

    const handleChange = (field: string, value: any) => {
        console.log(field, value);
        setUpdating(true);
        props.onChange(field, value);
    };

    const handleSave = () => {
        setUpdating(false);
        props.onSave();
    };

    const getDescription = (): React.ReactNode => {
        if(props.scene.type === SceneType.CRYPTO) {
            const sceneCustom: SceneCryptoType = props.scene as SceneCryptoType;

            return (
                <>
                    {sceneCustom.coins.join(', ')}
                </>
            );
        }
        else if(props.scene.type === SceneType.TEXT) {
            const sceneCustom: SceneTextType = props.scene as SceneTextType;

            return (
                <>
                    "{sceneCustom.lines.map((line: TextLine) => line.text).join(' ')}"
                </>
            );
        }
        else if(props.scene.type === SceneType.CLOCK) {
            const sceneCustom: SceneClockType = props.scene as SceneClockType;

            return (
                <>
                    
                </>
            );
        }
        else if(props.scene.type === SceneType.WEATHER) {
            const sceneCustom: SceneWeatherType = props.scene as SceneWeatherType;
            const placeName: string = sceneCustom.place_name;

            return (
                <>
                    {placeName}
                </>
            );
        }
        else if(props.scene.type === SceneType.SPOTIFY) {
            const credentials: UserApiCredential|null = user?.api_credentials.find((value: UserApiCredential) => value.type === 'SPOTIFY') ?? null;

            return (
                <>
                    {credentials === null || !credentials.authorized ? <><FaTriangleExclamation/>{t('page.account.api_credentials.account_not_linked')}</> : ''}
                </>
            );
        }
        else if(props.scene.type === SceneType.STOCK) {
            const sceneCustom: SceneStockType = props.scene as SceneStockType;

            return (
                <>
                    {sceneCustom.stocks.join(', ')}
                </>
            );
        }
        else if(props.scene.type === SceneType.TWITCH) {
            const sceneCustom: SceneTwitchType = props.scene as SceneTwitchType;

            return (
                <>
                    {sceneCustom.streamers?.map(s => s.streamer_name ?? s.streamer_login).join(', ')}
                </>
            );
        }
        return <></>
    };

    return (
        <div className="scene-container">
            <Card className={`scene-container-selector ${props.new ? 'new' : ''} ${props.scene.enabled ? '' : 'disabled'}`} noBorderRadiusBottom={isOpen} onClick={() => setOpen(!isOpen)} color="primary">
                <div className="row align-items-center">
                    <div className="col-auto">
                        {
                            props.scene.type === SceneType.CRYPTO ? <FaBitcoin size="2rem"/> :
                            props.scene.type === SceneType.TEXT ? <MdTextsms size="2rem"/> :
                            props.scene.type === SceneType.CLOCK ? <FaClock size="2rem"/> :
                            props.scene.type === SceneType.WEATHER ? <FaCloudSun size="2rem"/> :
                            props.scene.type === SceneType.GIF ? <FaImage size="2rem"/> :
                            props.scene.type === SceneType.OFF ? <FaEyeSlash size="2rem"/> :
                            props.scene.type === SceneType.SPOTIFY ? <FaSpotify size="2rem"/> :
                            props.scene.type === SceneType.COUNTDOWN ? <FaHourglassStart size="2rem"/> :
                            props.scene.type === SceneType.CANVAS ? <FaPaintbrush size="2rem"/> :
                            props.scene.type === SceneType.STOCK ? <BsGraphUpArrow size="2rem"/> :
                            props.scene.type === SceneType.TWITCH ? <FaTwitch size="2rem"/> :
                            <FaQuestion size="2rem"/>
                        }
                    </div>
                    <div className="col">
                        <div className="scene-header-title">{props.scene.type < typeLabels.length ? typeLabels[props.scene.type] : ''}</div>
                        <div className="scene-header-description">
                            <div className="description-duration"><FaRegClock size="0.8rem"/> {props.scene.display_duration}s</div>
                            <div className="description-text">{getDescription()}</div>
                        </div>
                    </div>
                    <div className="col-auto scene-header-actions">
                        <button
                            type="button"
                            className="scene-action-btn"
                            title={t('page.scenes.move_up')}
                            disabled={props.isFirst}
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); props.onMoveUp(); }}>
                            <FaAngleUp size="1.1rem"/>
                        </button>
                        <button
                            type="button"
                            className="scene-action-btn"
                            title={t('page.scenes.move_down')}
                            disabled={props.isLast}
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); props.onMoveDown(); }}>
                            <FaAngleDown size="1.1rem"/>
                        </button>
                        <button
                            type="button"
                            className={`scene-action-btn ${props.scene.enabled ? '' : 'off'}`}
                            title={props.scene.enabled ? t('page.scenes.disable') : t('page.scenes.enable')}
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); props.onToggleEnabled(!props.scene.enabled); }}>
                            {props.scene.enabled ? <FaEye size="1.1rem"/> : <FaEyeSlash size="1.1rem"/>}
                        </button>
                    </div>
                    <div className="col-auto">
                        {isOpen ? <FaAngleDown size="1.2rem"/> : <FaAngleRight size="1.2rem"/>}
                    </div>
                </div>
            </Card>

            {isOpen && (
                <Card noBorderRadiusTop>
                    {props.scene.type === SceneType.CRYPTO && (
                        <SceneCrypto scene={props.scene as SceneCryptoType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.TEXT && (
                        <SceneText scene={props.scene as SceneTextType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.CLOCK && (
                        <SceneClock scene={props.scene as SceneClockType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.WEATHER && (
                        <SceneWeather scene={props.scene as SceneWeatherType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.GIF && (
                        <SceneGif scene={props.scene as SceneGifType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.OFF && (
                        <SceneOff scene={props.scene as SceneOffType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.SPOTIFY && (
                        <SceneSpotify scene={props.scene as SceneSpotifyType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.COUNTDOWN && (
                        <SceneCountdown scene={props.scene as SceneCountdownType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.CANVAS && (
                        <SceneCanvas scene={props.scene as SceneCanvasType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.STOCK && (
                        <SceneStock scene={props.scene as SceneStockType} onChange={handleChange}/>
                    )}
                    {props.scene.type === SceneType.TWITCH && (
                        <SceneTwitch scene={props.scene as SceneTwitchType} onChange={handleChange}/>
                    )}

                    <div className="row mt-3 gx-2 gy-2 justify-content-end">
                        <div className="col-auto">
                            <Button className="secondary" onClick={props.onDelete}>{t('page.scenes.delete_scene')}</Button>
                        </div>
                        <div className="col-auto">
                            <Button disabled={!updating} onClick={handleSave}>{t('text.save')}</Button>
                        </div>
                    </div>
                </Card>
            )}
        </div>
    );
}

export default Scene;
